import { useState, useEffect, useRef } from 'react';
import {
  WbButton,
  WbCardContent,
  WbWidget,
  WbPagination,
  WbTooltip,
} from '@agilelab/plugin-wb-platform';
import { configApiRef, useApi } from '@mainstage/core-plugin-api';
import { IconButton, Theme } from '@material-ui/core';
import AddIcon from '@material-ui/icons/AddCircle';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDropDown';
import ArrowUpwardIcon from '@material-ui/icons/ArrowDropUp';
import RemoveIcon from '@material-ui/icons/Delete';
import { createStyles, makeStyles } from '@material-ui/styles';
import {
  ArrayFieldTemplateProps,
  getTemplate,
  getUiOptions,
} from '@rjsf/utils';
import {
  isHidden,
  UI_TABLE_ROW_TEMPLATE_ORIGINAL_TITLE_KEY,
} from '../../utils';

// TODO manuel: unify with wb-marketplace stylings, once we have a common core plugin
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    label: {
      color: theme.palette.text.secondary,
      textTransform: 'uppercase',
      fontSize: theme.typography.fontSize * 1.2,
      fontWeight: 'bold',
      letterSpacing: 0.5,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
    },
    text: {
      overflow: 'hidden',
      fontSize: theme.typography.fontSize * 1.1,
      wordBreak: 'break-word',
    },
  }),
);

const DefaultArrayItem = (props: any) => {
  return (
    <div
      key={props.key}
      style={{ display: 'flex', width: '100%', background: 'white' }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          border: '1px solid #cbcbcb',
          padding: '12px 16px',
          flex: '1',
          minWidth: 0,
        }}
      >
        {props.children}
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          paddingLeft: 20,
          paddingRight: 20,
          borderLeft: 'none',
          background: 'rgb(238 238 238 / 30%)',
        }}
      >
        {(props.hasMoveUp || props.hasMoveDown) && (
          <WbTooltip title="Move Up" placement="bottom-start">
            <IconButton
              color="secondary"
              size="small"
              disabled={props.disabled || props.readonly || !props.hasMoveUp}
              onClick={props.onReorderClick(props.index, props.index - 1)}
              style={{ padding: 0 }}
              aria-label="reorder up"
            >
              <ArrowUpwardIcon fontSize="medium" />
            </IconButton>
          </WbTooltip>
        )}

        {(props.hasMoveUp || props.hasMoveDown) && (
          <WbTooltip title="Move Down" placement="bottom-start">
            <IconButton
              color="secondary"
              size="small"
              disabled={props.disabled || props.readonly || !props.hasMoveDown}
              onClick={props.onReorderClick(props.index, props.index + 1)}
              style={{ padding: 0 }}
              aria-label="reorder down"
            >
              <ArrowDownwardIcon fontSize="medium" />
            </IconButton>
          </WbTooltip>
        )}

        {(props.hasMoveUp || props.hasMoveDown) && (
          <div
            style={{
              height: '20px',
              borderRight: '1px solid #cbcbcb',
              margin: '0px 5px',
            }}
          />
        )}

        {props.hasRemove && (
          <WbTooltip title="Remove Item" placement="bottom-start">
            <IconButton
              size="small"
              color="secondary"
              disabled={props.disabled || props.readonly}
              onClick={props.onDropIndexClick(props.index)}
              aria-label="remove"
            >
              <RemoveIcon fontSize="small" />
            </IconButton>
          </WbTooltip>
        )}
      </div>
    </div>
  );
};

export function DefaultArrayFieldTemplate(props: ArrayFieldTemplateProps) {
  const classes = useStyles();
  const uiOptions = getUiOptions(props.uiSchema);
  const DescriptionField = getTemplate(
    'DescriptionFieldTemplate',
    props.registry,
    uiOptions,
  );

  const configApi = useApi(configApiRef);
  const configuredRowsPerPage = configApi.getOptionalNumber(
    'mesh.builder.scaffolder.arrayFields.rowsPerPage',
  );
  const rowsPerPage =
    configuredRowsPerPage &&
    Number.isFinite(configuredRowsPerPage) &&
    configuredRowsPerPage > 0
      ? Math.floor(configuredRowsPerPage)
      : undefined;

  const [pagination, setPagination] = useState({
    limit: rowsPerPage ?? 0,
    offset: 0,
  });

  const totalItems = props.items?.length || 0;
  const previousTotalItemsRef = useRef(totalItems);

  useEffect(() => {
    const previousTotalItems = previousTotalItemsRef.current;

    if (!rowsPerPage) {
      previousTotalItemsRef.current = totalItems;
      return;
    }

    const lastPageOffset =
      totalItems > 0
        ? Math.max(0, Math.ceil(totalItems / rowsPerPage) - 1) * rowsPerPage
        : 0;

    setPagination(prev => {
      // Reset pagination when rowsPerPage changes.
      if (prev.limit !== rowsPerPage) {
        return { limit: rowsPerPage, offset: 0 };
      }

      // Move to the page containing the new item after additions.
      if (totalItems > previousTotalItems) {
        return prev.offset === lastPageOffset
          ? prev
          : { limit: rowsPerPage, offset: lastPageOffset };
      }

      // After removals, clamp to the last available page instead of jumping to page 0.
      if (prev.offset >= totalItems) {
        return prev.offset === lastPageOffset
          ? prev
          : { ...prev, offset: lastPageOffset };
      }

      return prev;
    });

    previousTotalItemsRef.current = totalItems;
  }, [rowsPerPage, totalItems]);

  const startIndex = pagination.offset;
  const endIndex = rowsPerPage
    ? Math.min(startIndex + pagination.limit, totalItems)
    : totalItems;
  const paginatedItems = rowsPerPage
    ? props.items?.slice(startIndex, endIndex) || []
    : props.items || [];

  return (
    <WbWidget
      title={
        props.uiSchema?.['ui:title'] ||
        // When in a TableRowTemplate, we want to show the original title of the field
        props.uiSchema?.[UI_TABLE_ROW_TEMPLATE_ORIGINAL_TITLE_KEY] ||
        props.title
      }
      cardStyle={{
        display: isHidden(props.uiSchema) ? 'none' : undefined,
        // having container-type: inline-size here causes issues making the card collapse insteaf of fitting all its content
        containerType: 'normal',
      }}
      actions={
        props.canAdd ? (
          <WbButton
            label="Add"
            onClick={props.onAddClick}
            icon={<AddIcon />}
            disabled={
              props.disabled ||
              props.readonly ||
              (Array.isArray(props.formData) &&
                props.formData.some(
                  item =>
                    item === undefined ||
                    item === null ||
                    (typeof item === 'string' && item.trim() === ''),
                ))
            }
            color="secondary"
          />
        ) : undefined
      }
      footer={
        totalItems > 0 && rowsPerPage ? (
          <WbPagination
            count={totalItems}
            offset={pagination.offset}
            limit={rowsPerPage}
            rowsPerPageOptions={[]}
            onPageChange={newPage => {
              setPagination({
                limit: rowsPerPage,
                offset: newPage * rowsPerPage,
              });
            }}
            onRowsPerPageChange={() => undefined}
          />
        ) : undefined
      }
    >
      <WbCardContent
        style={{
          background: 'rgb(238 238 238 / 30%)',
        }}
      >
        <div key={`array-item-list-${props.idSchema.$id}`}>
          {(props.uiSchema?.['ui:description'] || props.schema.description) && (
            <div>
              <DescriptionField
                id={`${props.idSchema.$id}-array-description`}
                description={
                  props.uiSchema?.['ui:description'] ||
                  props.schema.description ||
                  ''
                }
                schema={props.schema}
                registry={props.registry}
              />
            </div>
          )}

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 15,
              marginTop: 10,
            }}
          >
            {paginatedItems.map((element: any) =>
              // eslint-disable-next-line new-cap
              DefaultArrayItem({
                ...element,
                classes,
              }),
            )}
          </div>
        </div>
      </WbCardContent>
    </WbWidget>
  );
}
